import React from 'react';
import { Platform, Text as RNText, TextStyle } from 'react-native';
import { twMerge } from 'tailwind-merge';
import { mapFontWeights } from './helper';

//#region Types

type HeadlineVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
type CallToActionVariant = 'normal' | 'small';

// Discriminated unions
type HeadlineProps = {
  variant: 'headline';
  size: HeadlineVariant;
};

type CallToActionProps = {
  variant: 'callToAction';
  size: CallToActionVariant;
};

type BodyProps =
  | {
      variant: 'body';
      size: 'tiny';
      weight: 'regular';
    }
  | {
      variant: 'body';
      size: 'small';
      weight: 'regular' | 'bold';
    }
  | {
      variant: 'body';
      size: 'medium';
      weight: 'regular' | 'bold';
    }
  | {
      variant: 'body';
      size: 'large';
      weight: 'regular' | 'bold' | 'medium';
    };

type CustomProps = {
  variant: 'custom';
  size: 'chip' | 'smallChip';
};

type TypographyStyleProps =
  | HeadlineProps
  | CallToActionProps
  | BodyProps
  | CustomProps;

export type TypographyProps = TypographyStyleProps & {
  className?: string;
  children: React.ReactNode;
  fontSize?: number;
  lineHeight?: number;
};

//#region Variants

const headlineStyles = {
  h1: { fontSize: 34, fontWeight: '600', lineHeight: 41 },
  h2: { fontSize: 28, fontWeight: '600', lineHeight: 34 },
  h3: { fontSize: 24, fontWeight: '600', lineHeight: 31 },
  h4: { fontSize: 20, fontWeight: '600', lineHeight: 25 },
  h5: { fontSize: 17, fontWeight: '600', lineHeight: 22 },
} as const;

const callToActionStyles = {
  normal: { fontSize: 16, fontWeight: '600', lineHeight: 21 },
  small: { fontSize: 13, fontWeight: '600', lineHeight: 18 },
} as const;

const bodyStyles = {
  large: {
    regular: { fontSize: 15, fontWeight: '400', lineHeight: 20 },
    bold: { fontSize: 15, fontWeight: '700', lineHeight: 20 },
    medium: { fontSize: 15, fontWeight: '500', lineHeight: 20 },
  },
  medium: {
    regular: { fontSize: 14, fontWeight: '400', lineHeight: 20 },
    bold: { fontSize: 14, fontWeight: '700', lineHeight: 20 },
  },
  small: {
    regular: { fontSize: 13, fontWeight: '400', lineHeight: 18 },
    bold: { fontSize: 13, fontWeight: '700', lineHeight: 18 },
  },
  tiny: {
    regular: { fontSize: 11, fontWeight: '400', lineHeight: 13 },
  },
} as const;

const customStyles = {
  chip: { fontSize: 14, fontWeight: '600', lineHeight: 17 },
  smallChip: { fontSize: 12, fontWeight: '600', lineHeight: 20 },
} as const;

export function getTypographyStyle(props: TypographyStyleProps): TextStyle {
  if (props.variant === 'headline') {
    return headlineStyles[props.size];
  } else if (props.variant === 'callToAction') {
    return callToActionStyles[props.size];
  } else if (props.variant === 'body') {
    // TODO: find out why typescript gives errors here
    // @ts-ignore
    return bodyStyles[props.size][props.weight];
  } else if (props.variant === 'custom') {
    return customStyles[props.size];
  }

  return {};
}
//#endregion

//#region Component

function Typography(props: TypographyProps) {
  const { variant, children, className, fontSize, lineHeight } = props;

  let style: TextStyle = {
    fontFamily: 'Bozon',
    ...getTypographyStyle(props),
  };

  style = mapFontWeights(style);

  // TODO: i don't like this overrides. We should have all vaid font sizes in the style guide
  if (fontSize) style.fontSize = fontSize;

  if (lineHeight) style.lineHeight = lineHeight;

  // This has no functinal purpose (except maybe accessibility), its just to make the html look nicer
  const htmlRole =
    Platform.OS == 'web'
      ? variant == 'headline'
        ? 'heading'
        : 'paragraph'
      : null;

  return (
    <RNText
      style={style}
      className={twMerge(className)}
      // TODO: 'paragraph' seems to not be in the type definitions even though it definitely works, at least on web
      // @ts-ignore
      role={htmlRole}
    >
      {children}
    </RNText>
  );
}

export default Typography;
